import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EmailIcon from '@mui/icons-material/Email';
import { invoicesApi, Invoice } from '../services/invoices';
import { clientsApi, Client } from '../services/clients';
import { useCompany } from '../contexts/CompanyContext';
import { formatCurrency } from '../utils/currency';
import { format, parseISO } from 'date-fns';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const InvoiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const { company, baseCurrency } = useCompany();

  useEffect(() => {
    if (id) {
      loadInvoice();
    }
  }, [id]);

  const loadInvoice = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      const invoiceData = await invoicesApi.getById(id);
      setInvoice(invoiceData);
      
      // Load client data
      const clientData = await clientsApi.getById(invoiceData.clientId);
      setClient(clientData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (invoice && invoice.status === 'draft') {
      navigate(`/invoices/${invoice.id}/edit`);
    }
  };

  const handleMarkAsSent = async () => {
    if (!invoice) return;
    
    try {
      await invoicesApi.update(invoice.id, { status: 'sent' });
      loadInvoice();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to update invoice status');
    }
  };

  const handleOpenEmailDialog = () => {
    if (!invoice || !client) return;
    
    // Get primary contact email or first contact email
    const primaryEmail = client.contacts.find(c => c.email)?.email || '';
    setEmailTo(primaryEmail);
    
    // Generate default subject and body
    const defaultSubject = `Invoice ${invoice.invoiceNumber} from ${company?.name || 'Company'}`;
    const defaultBody = `Dear ${client.name},\n\nPlease find attached invoice ${invoice.invoiceNumber}.\n\nTotal Amount: ${invoice.currency} ${invoice.total.toFixed(2)}\nDue Date: ${format(parseISO(invoice.dueDate), 'MMM d, yyyy')}\n\nThank you for your business.\n\nBest regards,\n${company?.name || 'Company'}`;
    
    setEmailSubject(defaultSubject);
    setEmailBody(defaultBody);
    setEmailDialogOpen(true);
  };

  const handleCloseEmailDialog = () => {
    setEmailDialogOpen(false);
    setEmailTo('');
    setEmailSubject('');
    setEmailBody('');
  };

  const handleSendEmail = async () => {
    if (!invoice || !emailTo) return;
    
    try {
      setSendingEmail(true);
      setError(null);
      await invoicesApi.sendEmail(invoice.id, {
        to: emailTo,
        subject: emailSubject || undefined,
        body: emailBody || undefined,
      });
      handleCloseEmailDialog();
      loadInvoice();
      alert('Invoice sent successfully!');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send invoice email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExportPDF = async () => {
    if (!invoice) return;
    
    const element = document.getElementById('invoice-document');
    if (!element) return;

    try {
      // Show loading state
      const loadingAlert = document.createElement('div');
      loadingAlert.textContent = 'Generating PDF...';
      loadingAlert.style.position = 'fixed';
      loadingAlert.style.top = '50%';
      loadingAlert.style.left = '50%';
      loadingAlert.style.transform = 'translate(-50%, -50%)';
      loadingAlert.style.padding = '20px';
      loadingAlert.style.backgroundColor = 'white';
      loadingAlert.style.border = '1px solid #ccc';
      loadingAlert.style.borderRadius = '4px';
      loadingAlert.style.zIndex = '10000';
      document.body.appendChild(loadingAlert);

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`Invoice-${invoice.invoiceNumber}.pdf`);
      document.body.removeChild(loadingAlert);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try printing instead.');
    }
  };

  const getStatusColor = (status: Invoice['status']) => {
    switch (status) {
      case 'draft':
        return 'default';
      case 'sent':
        return 'info';
      case 'paid':
        return 'success';
      case 'overdue':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !invoice) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/invoices')}
          sx={{ mb: 2 }}
        >
          Back to Invoices
        </Button>
        <Alert severity="error">{error || 'Invoice not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/invoices')}
        >
          Back to Invoices
        </Button>
        <Box display="flex" gap={1}>
          {invoice.status === 'draft' && (
            <>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleEdit}
              >
                Edit
              </Button>
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={handleMarkAsSent}
              >
                Mark as Sent
              </Button>
            </>
          )}
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
          >
            Print
          </Button>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleExportPDF}
          >
            Export PDF
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<EmailIcon />}
            onClick={handleOpenEmailDialog}
            disabled={!client || !client.contacts.some(c => c.email)}
          >
            Send Email
          </Button>
        </Box>
      </Box>

      {/* Invoice Document */}
      <Paper sx={{ p: 4 }} id="invoice-document">
        {/* Company Header */}
        {company && (
          <Box mb={4}>
            {company.logoUrl && (
              <Box mb={2}>
                <img
                  src={company.logoUrl}
                  alt={company.name}
                  style={{ maxHeight: '80px', maxWidth: '200px' }}
                />
              </Box>
            )}
            <Typography variant="h5" fontWeight="bold">
              {company.name}
            </Typography>
            {company.location && (
              <Typography variant="body2" color="text.secondary">
                {company.location}
              </Typography>
            )}
          </Box>
        )}

        <Grid container spacing={4} mb={4}>
          {/* Invoice Info */}
          <Grid item xs={12} md={6}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Invoice {invoice.invoiceNumber}
            </Typography>
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                Issue Date: {format(parseISO(invoice.issueDate), 'MMMM dd, yyyy')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Due Date: {format(parseISO(invoice.dueDate), 'MMMM dd, yyyy')}
              </Typography>
              <Box mt={1}>
                <Chip
                  label={invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                  color={getStatusColor(invoice.status) as any}
                  size="small"
                />
              </Box>
            </Box>
          </Grid>

          {/* Bill To */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Bill To:
            </Typography>
            {client && (
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  {client.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {client.billingAddress.street}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {client.billingAddress.city}
                  {client.billingAddress.state && `, ${client.billingAddress.state}`}
                  {client.billingAddress.postalCode && ` ${client.billingAddress.postalCode}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {client.billingAddress.country}
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Line Items */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell align="right">
                  {invoice.timesheetId || (invoice.notes && (invoice.notes.toLowerCase().includes('timesheet') || invoice.notes.toLowerCase().includes('weekly')))
                    ? 'Quantity Hours (Days)'
                    : 'Quantity'}
                </TableCell>
                <TableCell align="right">Rate</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoice.lineItems.map((item, index) => {
                const isTimesheetInvoice = invoice.timesheetId || (invoice.notes && (invoice.notes.toLowerCase().includes('timesheet') || invoice.notes.toLowerCase().includes('weekly')));
                const workingHoursPerDay = client?.workingHoursPerDay || 8;
                const quantityDisplay = isTimesheetInvoice
                  ? `${item.quantity.toFixed(2)} (${(item.quantity / workingHoursPerDay).toFixed(2)} Days)`
                  : item.quantity.toString();
                
                return (
                  <TableRow key={index}>
                    <TableCell>{item.description}</TableCell>
                    <TableCell align="right">{quantityDisplay}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(item.rate, invoice.currency || baseCurrency)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(item.amount, invoice.currency || baseCurrency)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Totals */}
        <Box display="flex" justifyContent="flex-end" mt={3}>
          <Box sx={{ minWidth: '250px' }}>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body1">Subtotal:</Typography>
              <Typography variant="body1">
                {formatCurrency(invoice.subtotal, invoice.currency || baseCurrency)}
              </Typography>
            </Box>
            {invoice.tax > 0 && (
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body1">Tax:</Typography>
                <Typography variant="body1">
                  {formatCurrency(invoice.tax, invoice.currency || baseCurrency)}
                </Typography>
              </Box>
            )}
            <Divider sx={{ my: 1 }} />
            <Box display="flex" justifyContent="space-between">
              <Typography variant="h6" fontWeight="bold">
                Total:
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatCurrency(invoice.total, invoice.currency || baseCurrency)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Notes */}
        {invoice.notes && (
          <Box mt={4}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Notes:
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
              {invoice.notes}
            </Typography>
          </Box>
        )}

        {/* Bank Details & Payment Information */}
        {company && (
          <Box mt={4}>
            {(company.bankAccountName || company.bankAccountNumber || company.bankSortCode || 
              company.bankIban || company.bankBic || company.bankCurrencyAccepted ||
              company.contactPhone || company.contactEmail) && (
              <>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Payment Information:
                </Typography>
                
                {/* UK Bank Account Details */}
                {(company.bankAccountName || company.bankAccountNumber || company.bankSortCode) && (
                  <Box mt={2} mb={2}>
                    <Typography variant="body2" fontWeight="medium" gutterBottom>
                      UK Bank Account:
                    </Typography>
                    {company.bankAccountName && (
                      <Typography variant="body2" color="text.secondary">
                        Account Name: {company.bankAccountName}
                      </Typography>
                    )}
                    {company.bankAccountNumber && (
                      <Typography variant="body2" color="text.secondary">
                        Account Number: {company.bankAccountNumber}
                      </Typography>
                    )}
                    {company.bankSortCode && (
                      <Typography variant="body2" color="text.secondary">
                        Sort Code: {company.bankSortCode}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* International Payment Details */}
                {(company.bankIban || company.bankBic || company.bankCurrencyAccepted) && (
                  <Box mt={2} mb={2}>
                    <Typography variant="body2" fontWeight="medium" gutterBottom>
                      International Payments:
                    </Typography>
                    {company.bankCurrencyAccepted && (
                      <Typography variant="body2" color="text.secondary">
                        Currency Accepted: {company.bankCurrencyAccepted}
                      </Typography>
                    )}
                    {company.bankIban && (
                      <Typography variant="body2" color="text.secondary">
                        IBAN: {company.bankIban}
                      </Typography>
                    )}
                    {company.bankBic && (
                      <Typography variant="body2" color="text.secondary">
                        BIC: {company.bankBic}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Contact Details */}
                {(company.contactPhone || company.contactEmail) && (
                  <Box mt={2} mb={2}>
                    <Typography variant="body2" fontWeight="medium" gutterBottom>
                      Contact Details:
                    </Typography>
                    {company.contactPhone && (
                      <Typography variant="body2" color="text.secondary">
                        Phone: {company.contactPhone}
                      </Typography>
                    )}
                    {company.contactEmail && (
                      <Typography variant="body2" color="text.secondary">
                        Email: {company.contactEmail}
                      </Typography>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>
        )}

        {/* Payment Terms */}
        {company && (
          <Box mt={4}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Payment Terms:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Payment is due within 30 days of the invoice date. Late payments may be subject to statutory interest 
              at a rate of UK Base Rate + 8% per annum, in accordance with the Late Payment of Commercial Debts (Interest) Act 1998.
            </Typography>
          </Box>
        )}

        {/* Email Sent Info */}
        {invoice.sentDate && (
          <Box mt={4}>
            <Alert severity="info">
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Invoice Sent
              </Typography>
              <Typography variant="body2">
                Sent to: {invoice.sentToEmail || 'N/A'}
              </Typography>
              <Typography variant="body2">
                Sent on: {format(parseISO(invoice.sentDate), 'MMM d, yyyy HH:mm')}
              </Typography>
            </Alert>
          </Box>
        )}
      </Paper>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #invoice-document, #invoice-document * {
            visibility: visible;
          }
          #invoice-document {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          button {
            display: none !important;
          }
        }
      `}</style>

      {/* Send Email Dialog */}
      <Dialog open={emailDialogOpen} onClose={handleCloseEmailDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Send Invoice via Email</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            <TextField
              fullWidth
              label="To"
              type="email"
              required
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              helperText="Email address to send the invoice to"
            />
            <TextField
              fullWidth
              label="Subject"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              helperText="Email subject line"
            />
            <TextField
              fullWidth
              label="Message"
              multiline
              rows={6}
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              helperText="Email message body (invoice PDF will be attached)"
            />
            {invoice && (
              <Alert severity="info">
                The invoice PDF will be automatically attached to this email.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEmailDialog} disabled={sendingEmail}>
            Cancel
          </Button>
          <Button
            onClick={handleSendEmail}
            variant="contained"
            disabled={!emailTo || sendingEmail}
            startIcon={<EmailIcon />}
          >
            {sendingEmail ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceDetail;







